import React, { Fragment, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import Icon from '../../../Icon';
import { enhance } from '../enhance';
import Popup from '../../../common/Popup';
import PharmacyForm from '../PharmacyForm';
import Loader from '../../../common/Loader';
import { useLazyLoad } from '../../../../hocs/useLazyLoad';
import { isDesktop } from '../../../../utils/mobileHelpers';
import EmptyContent from '../../../common/EmptyState/Content';
import { makePharmacyTitle } from '../../../../utils/makePharmacyTitle';

import styles from './styles.styl';

function PharmacyPopup({
  isOpen,
  onClose,
  setPharmacy,
  pharmaciesList,
  getPharmaciesList,
  clearPharmacyList,
  loadMorePharmacies,
  setPreferredPharmacy,
  intl: { formatMessage },
  withMailOrder = true,
}) {
  const rootRef = useRef();
  const offsetRef = useRef(0);
  const [picked, setPicked] = useState({});
  const [formData, setFormData] = useState('');
  const [activeMailOrder, setActiveMailOrder] = useState(false);

  const loadMore = useCallback(() => {
    loadMorePharmacies({
      zip: formData,
      offset: offsetRef.current,
      activeMailOrder,
    });
    offsetRef.current += 20;
  }, [formData, activeMailOrder]);

  useLazyLoad({ node: rootRef, loadMore, path: [] });

  const handleSubmit = e => {
    e.preventDefault();
    setPreferredPharmacy(picked);
    setPharmacy(picked);
    onClose();
  };

  const fetchPharmacies = (value, order) => {
    if (!value.trim()) {
      return;
    }

    offsetRef.current = 0;
    clearPharmacyList();

    getPharmaciesList({
      zip: value,
      limit: 20,
      offset: offsetRef.current,
      ...(order && {
        mailOrder: order,
      }),
    });
  };

  const handleFormChange = debounce(text => {
    if (text.length > 9) return;
    setFormData(text);
    if (text.length < 3) {
      clearPharmacyList();
      return;
    }

    fetchPharmacies(text, activeMailOrder);
  }, 1000);

  const handleCheck = () => {
    fetchPharmacies(formData, !activeMailOrder);
    setActiveMailOrder(!activeMailOrder);
  };

  const emptyContentText =
    formData && formData.length > 2
      ? 'patient.portal.healthRecord.emptyState.nothingFound'
      : 'appointments.form.emptyPharmacyList';

  const renderDialogHeader = (
    <PharmacyForm
      id={picked.id}
      formData={formData}
      handleSubmit={handleSubmit}
      withMailOrder={withMailOrder}
      activeMailOrder={activeMailOrder}
      handleCheckboxToggle={handleCheck}
      handleChange={value => handleFormChange(value)}
    />
  );
  const pharmaciesCount =
    pharmaciesList.count > 1
      ? `${pharmaciesList.count} ${formatMessage({
          id: 'appointments.pharmacies',
        })}`
      : `${pharmaciesList.count} ${formatMessage({
          id: 'patient.portal.medications.pharmacy',
        }).toLowerCase()}`;

  return (
    <Popup
      strictClose
      contentWhite
      overflowVisible
      opened={isOpen}
      contentNoPadding
      onClose={onClose}
      paperClassName={styles.popupRoot}
      width={isDesktop() ? 'medium' : 'small'}
      dialogHeaderContent={renderDialogHeader}
      extraContentClassName={styles.extraContent}
      title={formatMessage({ id: 'appointments.form.searchForPharmacy' })}
    >
      <div className={styles.popupContent} ref={rootRef}>
        {(!pharmaciesList.count && !pharmaciesList.sending) || !formData ? (
          <div className={styles.emptyContent}>
            <EmptyContent
              text={formatMessage({
                id: emptyContentText,
              })}
              icon="search-list"
              className="absolute-center"
            />
          </div>
        ) : (
          <Fragment>
            {!pharmaciesList.sending && (
              <div className={styles.pharmacyCount}>{pharmaciesCount}</div>
            )}
            {pharmaciesList.sending && !pharmaciesList.succeed && <Loader />}
            <Fragment>
              {pharmaciesList.list.map(el => {
                return (
                  <div
                    key={el.id}
                    className={styles.pharmacyItem}
                    onClick={() => setPicked(el)}
                  >
                    <div>{makePharmacyTitle(el)}</div>
                    <span
                      className={
                        picked.id === el.id ? styles.showIcon : styles.hideIcon
                      }
                    >
                      <Icon name="check" fill="#3b557a" width="16" />
                    </span>
                  </div>
                );
              })}
            </Fragment>
          </Fragment>
        )}
      </div>
    </Popup>
  );
}

PharmacyPopup.propTypes = {
  intl: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  setPharmacy: PropTypes.func,
  withMailOrder: PropTypes.bool,
  pharmaciesList: PropTypes.object,
  getPharmaciesList: PropTypes.func,
  clearPharmacyList: PropTypes.func,
  loadMorePharmacies: PropTypes.func,
  setPreferredPharmacy: PropTypes.func,
};

export default enhance(PharmacyPopup);



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentPharmacy/PharmacyPopup/index.jsx